/**
 * Formatadores numéricos (§13.4.2), funções puras e a ÚNICA forma de transformar número em texto (§13.4). Locale
 * pt-BR fixo (nunca `navigator.language`), `Intl.NumberFormat` criado uma vez por configuração (cache em módulo).
 * Regras gerais de §13.4.1: decimal ","; milhar "."; negativo com U+2212; "+" só em deltas; ausente "–" (U+2013);
 * "%" colado; U+00A0 entre o número e a primeira palavra da unidade.
 *
 * Os textos das unidades ("milhões de votos", "pontos", "passou de 50%") moram aqui, e não em src/copy/strings.ts:
 * strings.ts importa este arquivo (t() formata `{N}` com fmtInt), e a volta faria ciclo (FE §4.2). As chaves `goal.*`
 * do catálogo têm o mesmo texto; quem preferir a chave usa `distanceTo50()` e escolhe a chave pelo `kind`.
 */

/** Valor ausente (U+2013): denominador 0 ou valor não finito. */
export const DASH = '–';
/** Sinal negativo exibido (U+2212); o hífen-menos ASCII nunca aparece na interface. */
export const MINUS = '−';
/** Espaço inseparável (U+00A0) entre o número e a primeira palavra da unidade. */
export const NBSP = ' ';

type Rounding = 'halfExpand' | 'trunc';

const cache = new Map<string, Intl.NumberFormat>();
function nf(min: number, max: number, mode: Rounding = 'halfExpand'): Intl.NumberFormat {
  const key = `${min}-${max}-${mode}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
      roundingMode: mode,
      useGrouping: true,
    } as Intl.NumberFormatOptions);
    cache.set(key, f);
  }
  return f;
}

/** Tira o ruído de ponto flutuante de produtos como 0,29 × 100 = 28,999…96 (15 algarismos significativos). */
const clean = (x: number): number => Number(x.toPrecision(15));

/** Texto sem dígito diferente de zero ("0", "0,0", "0,00"). */
const isZeroText = (s: string): boolean => !/[1-9]/.test(s);

/** |x| formatado (halfExpand) e o sinal de x; sinal 0 quando o texto arredondado é zero (nunca "−0,0"). */
function signed(x: number, decimals: number): { text: string; sign: -1 | 0 | 1 } {
  const text = nf(decimals, decimals).format(clean(Math.abs(x)));
  return { text, sign: isZeroText(text) ? 0 : x < 0 ? -1 : 1 };
}

const withMinus = (r: { text: string; sign: -1 | 0 | 1 }): string => (r.sign < 0 ? `${MINUS}${r.text}` : r.text);

// ── Inteiros e porcentagens ─────────────────────────────────────────────────────────────────────────────────

/** "53.879.538"; 0 → "0"; negativo com "−"; arredondamento halfExpand; não finito → "–". */
export function fmtInt(n: number): string {
  if (!Number.isFinite(n)) return DASH;
  return withMinus(signed(n, 0));
}

/** Partes de uma porcentagem para quem estiliza o "%" (herói, §7.5): `value + unit === fmtPct(...)`. */
export interface PctParts {
  /** "47,03" (ou "–" sem valor). */
  value: string;
  /** "%" (ou "" sem valor). */
  unit: '%' | '';
}

/** fmtPct em partes: { value: "47,03", unit: "%" }; não finito → { value: "–", unit: "" }. */
export function fmtPctParts(share: number, decimals: 1 | 2 = 1): PctParts {
  if (!Number.isFinite(share)) return { value: DASH, unit: '' };
  return { value: withMinus(signed(share * 100, decimals)), unit: '%' };
}

/** share × 100, halfExpand, casas fixas (padrão 1; herói e duelo 2): 0,4703 → "47,0%"; (0,47035, 2) → "47,04%". */
export function fmtPct(share: number, decimals: 1 | 2 = 1): string {
  const p = fmtPctParts(share, decimals);
  return `${p.value}${p.unit}`;
}

/** Casas do par: 2 quando os dois primeiros estão a menos de 1 ponto (|a − b| < 0,01), senão 1 (§13.4.2, §7.6). */
export function pctDecimals(a: number, b: number): 1 | 2 {
  return Math.abs(a - b) < 0.01 ? 2 : 1;
}

/** fmtPct com as casas de `pctDecimals(a, b)`: (0,5012; 0,5012; 0,4988) → "50,12%". */
export function fmtPctAdaptive(share: number, a: number, b: number): string {
  return fmtPct(share, pctDecimals(a, b));
}

/**
 * % das seções, TRUNCADO (§13.4.2; nunca arredonda para cima). Entrada: f = sections_counted / sections.
 * f = 1 → "100%"; f < 1 nunca "100%"; 0 → "0%"; 0 < f < 0,0001 → "menos de 0,01%"; f < 0,01 → 2 casas; senão 1.
 * `x = floor(f × 10^(d+2) + 1e-7)`: o 1e-7 só anula erro de ponto flutuante (0,345 × 1000 = 344,999…).
 */
export function fmtSections(fraction: number): string {
  if (!Number.isFinite(fraction) || fraction < 0) return DASH;
  if (fraction >= 1) return '100%';
  if (fraction === 0) return '0%';
  if (fraction < 0.0001) return 'menos de 0,01%';
  const d = fraction < 0.01 ? 2 : 1;
  const x = Math.floor(fraction * 10 ** (d + 2) + 1e-7) / 10 ** d;
  return `${nf(d, d, 'trunc').format(Math.min(x, 100 - 10 ** -d))}%`;
}

/** Rótulo curto (mapa, anéis): 1 → "100"; 0 < f < 0,01 → "<1"; 0 → "0"; senão inteiro truncado (0,834 → "83"). */
export function fmtSectionsCompact(fraction: number): string {
  if (!Number.isFinite(fraction) || fraction < 0) return DASH;
  if (fraction >= 1) return '100';
  if (fraction === 0) return '0';
  if (fraction < 0.01) return '<1';
  return String(Math.min(99, Math.floor(fraction * 100 + 1e-7)));
}

// ── Quantidades por extenso (votos, eleitores) ──────────────────────────────────────────────────────────────

type Unit = readonly [singular: string, pluralForm: string];
const VOTES: Unit = ['voto', 'votos'];
const VOTERS: Unit = ['eleitor', 'eleitores'];

/**
 * Escala comum (§13.4.2): n ≥ 999.500 → milhões com 1 casa (halfExpand), sem ",0", "milhão" quando a parte inteira
 * exibida é 1 e "milhões" a partir de 2, sempre com "de" antes da unidade; 10.000 ≤ n < 999.500 → round(n/1000) +
 * " mil"; n < 10.000 → fmtInt, unidade no singular só para "1". U+00A0 entre o número e a primeira palavra.
 */
function scaled(n: number, unit: Unit | null): string {
  if (!Number.isFinite(n)) return DASH;
  const abs = Math.abs(n);
  const sign = n < 0 ? MINUS : '';
  if (abs >= 999_500) {
    const text = nf(0, 1).format(clean(abs / 1_000_000));
    const word = text.split(',')[0] === '1' ? 'milhão' : 'milhões';
    return `${sign}${text}${NBSP}${word}${unit ? ` de ${unit[1]}` : ''}`;
  }
  if (abs >= 10_000) {
    const text = nf(0, 0).format(clean(abs / 1000));
    return `${sign}${text}${NBSP}mil${unit ? ` ${unit[1]}` : ''}`;
  }
  const r = signed(n, 0);
  const text = withMinus(r);
  if (!unit) return text;
  return `${text}${NBSP}${r.text === '1' ? unit[0] : unit[1]}`;
}

/** 2.214.889 → "2,2 milhões de votos" · 1.000.000 → "1 milhão de votos" · 15.412 → "15 mil votos" · 1 → "1 voto". */
export function fmtVotesLong(n: number): string {
  return scaled(n, VOTES);
}

/** Mesma escala com "eleitor"/"eleitores": 155.912.004 → "155,9 milhões de eleitores" · 1 → "1 eleitor". */
export function fmtVoters(n: number): string {
  return scaled(n, VOTERS);
}

/** Mesma escala, sem a unidade: 2.301.000 → "2,3 milhões" · 450.200 → "450 mil" · 9.876 → "9.876". */
export function fmtQtyShort(n: number): string {
  return scaled(n, null);
}

// ── Pontos, distância a 50%, deltas ─────────────────────────────────────────────────────────────────────────

/**
 * |diff| × 100 com casas fixas (herói 2; demais 1, o padrão) + " pontos"; singular só quando o número formatado é
 * "1,0" ou "1,00" (glossário, §18.2): 0,0187 (2) → "1,87 pontos" · (0,01, 1) → "1,0 ponto" · 0,004 → "0,4 pontos".
 */
export function fmtPoints(diff: number, decimals: 1 | 2 = 1): string {
  if (!Number.isFinite(diff)) return DASH;
  const text = nf(decimals, decimals).format(clean(Math.abs(diff) * 100));
  const one = text === (decimals === 1 ? '1,0' : '1,00');
  return `${text}${NBSP}${one ? 'ponto' : 'pontos'}`;
}

/** Situação do rodapé da GoalBar (§7.6): passou de 50%, empate exato, a menos de 1 casa, ou faltam `distance`. */
export interface DistanceTo50 {
  kind: 'passed' | 'tie' | 'near' | 'missing';
  /** 0,5 − share (fração; > 0 em `near` e `missing`, ≤ 0 nos demais). Formatar com `fmtPoints(distance, decimals)`. */
  distance: number;
}

/** Classifica a distância a 50% com as casas dadas (§13.4.2, §7.6); null sem valor. */
export function distanceTo50(share: number, decimals: 1 | 2): DistanceTo50 | null {
  if (!Number.isFinite(share)) return null;
  const distance = 0.5 - share;
  if (share > 0.5) return { kind: 'passed', distance };
  if (share === 0.5) return { kind: 'tie', distance: 0 };
  const text = nf(decimals, decimals).format(clean(distance * 100));
  return { kind: isZeroText(text) ? 'near' : 'missing', distance };
}

/**
 * Rodapé da GoalBar (§13.4.2, §7.6): "passou de 50%" · "em 50%" (votos iguais, `goal.tie`) · "a menos de 0,1 ponto"
 * (1 casa) / "a menos de 0,01 ponto" (2 casas) · "falta 1,0 ponto" · "faltam 4,8 pontos".
 */
export function fmtDistanceTo50(share: number, decimals: 1 | 2): string {
  const d = distanceTo50(share, decimals);
  if (d === null) return DASH;
  switch (d.kind) {
    case 'passed':
      return 'passou de 50%';
    case 'tie':
      return 'em 50%';
    case 'near':
      return `a menos de ${decimals === 1 ? '0,1' : '0,01'}${NBSP}ponto`;
    case 'missing': {
      const points = fmtPoints(d.distance, decimals);
      return `${points.endsWith(`${NBSP}ponto`) ? 'falta' : 'faltam'} ${points}`;
    }
  }
}

/** Pontos percentuais com 1 casa e sinal explícito: 0,037 → "+3,7" · −0,021 → "−2,1" · zero → "0,0". */
export function fmtDelta(pp: number): string {
  if (!Number.isFinite(pp)) return DASH;
  const r = signed(pp * 100, 1);
  if (r.sign === 0) return r.text;
  return `${r.sign > 0 ? '+' : MINUS}${r.text}`;
}

// ── Probabilidade (só no ProbabilitySlot, §14.6) ────────────────────────────────────────────────────────────

const round = (x: number): number => Math.round(clean(x));

/** Chance inteira de 1 a 99 (halfExpand); p < 0,005 → "<1"; p ≥ 0,995 → ">99". Nunca "0" nem "100". */
export function fmtChance(p: number): string {
  if (!Number.isFinite(p)) return DASH;
  if (p < 0.005) return '<1';
  if (p >= 0.995) return '>99';
  return String(Math.min(99, Math.max(1, round(p * 100))));
}

/**
 * Par de chances que soma 100: arredonda o maior (halfExpand) e deriva o menor como 100 − x; extremos viram ">99" e
 * "<1". Chances iguais → "50" e "50" (neutralidade: trocar a ordem das entradas só troca a ordem da saída).
 */
export function fmtChancePair(pA: number, pB: number): [string, string] {
  if (!Number.isFinite(pA) || !Number.isFinite(pB)) return [DASH, DASH];
  if (pA === pB) return ['50', '50'];
  const big = Math.max(pA, pB);
  let hi: string;
  let lo: string;
  if (big >= 0.995) {
    hi = '>99';
    lo = '<1';
  } else {
    const x = Math.min(99, Math.max(50, round(big * 100)));
    hi = String(x);
    lo = String(100 - x);
  }
  return pA > pB ? [hi, lo] : [lo, hi];
}

/**
 * O par de chances que a interface mostra (o único lugar da regra; P-30 item 1, §7.14 R5): ">99" e "<1" só com a
 * chamada "acima de 99%" aprovada (`called`); sem ela, cada chance fica entre 0,01 e 0,99 antes do arredondamento, e
 * o par sai no máximo "99" e "1". Nunca "0" nem "100" (fmtChancePair).
 */
export function fmtChancePairShown(pA: number, pB: number, called: boolean): [string, string] {
  const cap = (x: number) => (called ? x : Math.min(0.99, Math.max(0.01, x)));
  return fmtChancePair(cap(pA), cap(pB));
}

/**
 * Quantas das 10.000 simulações a chance exibida representa (ORD-F-151: "Em 10.000 simulações, {X} vence em {N}
 * ({P}%)"): {N} = {P} × 100, a partir do MESMO texto que o bloco mostra (fmtChancePairShown), para os dois nunca
 * discordarem; "94" → "9.400", "99" (o teto sem a chamada) → "9.900". ">99", "<1" e "–" não têm contagem exata → "–"
 * (quem chama usa a variante "mais de 9.900").
 */
export function fmtSimWins(shownChance: string): string {
  if (!/^\d{1,2}$/.test(shownChance)) return DASH;
  return fmtInt(Number(shownChance) * 100);
}

/** Quantis da projeção (estrutura de `Quantiles` de §14.6; folha não importa contracts). */
export interface ProjectionQuantiles {
  q10: number;
  q50: number;
  q90: number;
}

/** Mediana e faixa de 80% em % com 1 casa: q50 0,5183 → "51,8%"; faixa "entre {low} e {high}" fica no catálogo. */
export function fmtProjection(q: ProjectionQuantiles): { median: string; low: string; high: string } {
  return { median: fmtPct(q.q50, 1), low: fmtPct(q.q10, 1), high: fmtPct(q.q90, 1) };
}

// ── Leituras do caminho do voto (números como o motor gravou) ─────────────────────────────────────────────

/**
 * Número como o motor gravou, sem arredondar: até 6 casas (o motor já arredonda as medidas a 6), milhar "." e decimal
 * ",": 0.995025 → "0,995025" · -8879 → "−8.879" · 5.2e-05 → "0,000052". Não finito → "–".
 */
export function fmtExact(n: number): string {
  if (!Number.isFinite(n)) return DASH;
  const text = nf(0, 6).format(clean(Math.abs(n)));
  return withMinus({ text, sign: isZeroText(text) ? 0 : n < 0 ? -1 : 1 });
}

/**
 * Taxa por seção em porcentagem, com 2 algarismos significativos (só para leitura; o número exato vai ao lado em
 * fmtExact): 0.000052 → "0,0052%" · 0.4 → "40%" · 0 → "0%".
 */
export function fmtRatePct(rate: number): string {
  if (!Number.isFinite(rate)) return DASH;
  if (rate === 0) return '0%';
  const pct = clean(rate * 100);
  const digits = Math.max(0, Math.min(6, 1 - Math.floor(Math.log10(Math.abs(pct)))));
  const text = nf(0, digits).format(Math.abs(pct));
  return `${withMinus({ text, sign: isZeroText(text) ? 0 : pct < 0 ? -1 : 1 })}%`;
}

/** Até `max` casas (sem zeros à direita), milhar "." e decimal ",": (96.745283, 1) → "96,7" · (-0.38842, 2) → "−0,39". */
export function fmtDecimal(n: number, max = 2): string {
  if (!Number.isFinite(n)) return DASH;
  const text = nf(0, max).format(clean(Math.abs(n)));
  return withMinus({ text, sign: isZeroText(text) ? 0 : n < 0 ? -1 : 1 });
}

/** Como fmtDecimal, com "+" no positivo (inclinações e diferenças): 0.27 → "+0,27" · −0.39 → "−0,39" · 0 → "0". */
export function fmtSigned(n: number, max = 2): string {
  if (!Number.isFinite(n)) return DASH;
  const t = fmtDecimal(n, max);
  return t.startsWith(MINUS) || isZeroText(t) ? t : `+${t}`;
}

/** Casas fixas (z com duas casas): (−13.047, 2) → "−13,05" · (0.9, 2) → "0,90". */
export function fmtFixed(n: number, decimals = 2): string {
  if (!Number.isFinite(n)) return DASH;
  return withMinus(signed(n, decimals));
}
